/**
 * Speech I/O for the voice assistant — both directions are browser-native and
 * free (Web Speech API), no third-party account needed. Isolated in this one
 * file specifically so an upgrade to a paid voice (Isaac mentioned ElevenLabs)
 * is a rewrite of `speak()` alone — every caller just awaits it, unaware of
 * what's actually producing the audio.
 */

/** Safari (desktop and iOS) has no SpeechRecognition at all as of this
 * writing; Chrome/Edge expose it under the unprefixed or webkit- name. */
export function speechRecognitionSupported(): boolean {
  return Boolean(
    (window as unknown as { SpeechRecognition?: unknown }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition,
  );
}

export function speechSynthesisSupported(): boolean {
  return "speechSynthesis" in window;
}

/**
 * Picks a British-accented voice for the "Jarvis" persona, preferring a
 * male-sounding one since that's what the reference (Iron Man's Jarvis) is.
 * Browsers don't expose a real gender field, so this is a name heuristic —
 * good enough for the common installed voices (e.g. macOS/iOS "Daniel",
 * Chrome's "Google UK English Male"), harmless if it falls through: any
 * en-GB voice is still "British," and no en-GB voice at all just means the
 * browser's default speaks the answer instead of staying silent.
 */
export function pickJarvisVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const british = voices.filter((v) => v.lang?.toLowerCase().startsWith("en-gb"));
  if (british.length === 0) return null;
  const male = british.find((v) => !/female/i.test(v.name));
  return male ?? british[0] ?? null;
}

/** Voices load asynchronously in most browsers — the first call after page
 * load routinely returns an empty array, which is why every caller needs to
 * await this rather than reading `speechSynthesis.getVoices()` directly. */
function getVoicesAsync(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) {
      resolve(existing);
      return;
    }
    const onChange = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChange);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", onChange);
    // Some browsers never fire voiceschanged if voices were already loaded
    // synchronously elsewhere — don't hang the caller forever waiting.
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 1000);
  });
}

/** Speaks `text` aloud, resolving once playback finishes (or immediately if
 * speech synthesis isn't supported at all — callers still get their answer
 * displayed as text either way, this is a progressive enhancement). */
export async function speak(text: string): Promise<void> {
  if (!speechSynthesisSupported()) return;
  window.speechSynthesis.cancel(); // don't let two answers overlap
  const voices = await getVoicesAsync();
  const utterance = new SpeechSynthesisUtterance(text);
  const voice = pickJarvisVoice(voices);
  if (voice) utterance.voice = voice;
  utterance.rate = 1.0;
  utterance.pitch = 0.95;
  return new Promise((resolve) => {
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    window.speechSynthesis.speak(utterance);
  });
}

export function stopSpeaking(): void {
  if (speechSynthesisSupported()) window.speechSynthesis.cancel();
}

/**
 * Wraps the browser's SpeechRecognition in a promise: listens for one
 * utterance, resolves with the transcript. Rejects if unsupported or if the
 * user doesn't say anything before it times out.
 */
export function listenOnce(): Promise<string> {
  const Ctor =
    (window as unknown as { SpeechRecognition?: new () => SpeechRecognition }).SpeechRecognition ||
    (window as unknown as { webkitSpeechRecognition?: new () => SpeechRecognition })
      .webkitSpeechRecognition;
  if (!Ctor) return Promise.reject(new Error("speech_recognition_unsupported"));

  return new Promise((resolve, reject) => {
    const recognition = new Ctor();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0]?.[0]?.transcript;
      if (transcript) resolve(transcript);
      else reject(new Error("no_transcript"));
    };
    recognition.onerror = (event: SpeechRecognitionErrorEvent) => reject(new Error(event.error));
    recognition.onend = () => {
      // If onresult never fired (silence/timeout), onend still fires — reject
      // rather than leave the caller's promise hanging forever.
      reject(new Error("no_speech_detected"));
    };
    recognition.start();
  });
}
